// Hideo - local profiles: creating, editing and deleting a profile, and the profile info carried by a loaded file
// (logic without UI). The profile picker (ProfilePicker.vue) and the profile card (ProfileEditDialog.vue) use it through
// the profiles store (src/stores/profiles.ts); the list and its storage key are in src/data/profiles.ts.
import { SOCIALS, SYNC_KEY, cleanProfile, lsKey } from '../data/profiles'
import { normUrl } from '../utils/url'
import type { Profile, ProfilesState } from '../data/types'

/** Longest profile name (the name field's maxlength, cleanProfile) */
export const PROFILE_NAME_MAX = 40

/** Initial of a profile's circle when it has no photo */
export const profileInitial = (p: { name: string }) => (p.name.trim()[0] || 'P').toUpperCase()

type Social = (typeof SOCIALS)[number]
/** Fields of the profile card */
export type ProfileForm = { name: string; avatar: string; bio: string } & Record<Social, string>

/** Starting fields of the profile card: an existing profile, or a new one (id null: the name starts empty, not
 *  "Profile"). null when the profile does not exist */
export function profileForm(list: readonly Profile[], id: string | null): ProfileForm | null {
  const p = id ? list.find(x => x.id === id) : cleanProfile({ id: '', name: '' })
  if (!p) return null
  const f = { name: id ? p.name : '', avatar: p.avatar || '', bio: p.bio || '' } as ProfileForm
  SOCIALS.forEach(k => { f[k] = p[k] || '' })
  return f
}

/** What is wrong with a profile name: 'empty', 'taken' (another profile has it, any case), or null */
export function profileNameError(list: readonly Profile[], id: string, name: string): 'empty' | 'taken' | null {
  const n = name.trim()
  if (!n) return 'empty'
  return list.some(x => x.id !== id && x.name.toLowerCase() === n.toLowerCase()) ? 'taken' : null
}

/** Writes the profile card into the list: a new profile (id null, added at the end) or an existing one. Returns the
 *  profile, or an error ('missing' = the profile is gone) and nothing changes */
export function writeProfile(list: Profile[], id: string | null, f: ProfileForm, now = Date.now()):
  { profile: Profile; isNew: boolean } | { error: 'missing' | 'empty' | 'taken' } {
  const isNew = !id
  const p = isNew ? cleanProfile({ id: 'p_' + now, name: '' }) : list.find(x => x.id === id)
  if (!p) return { error: 'missing' }
  const err = profileNameError(list, p.id, f.name)
  if (err) return { error: err }
  p.name = f.name.trim()
  p.avatar = f.avatar
  p.bio = f.bio.trim()
  SOCIALS.forEach(k => { p[k] = normUrl(f[k]) })
  p.updatedAt = now
  if (isNew) list.push(p)
  return { profile: p, isNew }
}

/** Name, photo, bio and socials saved in a loaded file go into the profile (the name only if no other profile uses it) */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function applyProfileInfo(list: readonly Profile[], p: Profile, info: any, now = Date.now()): void {
  const c = cleanProfile({ ...info, id: p.id }), name = String(info.name || '').trim().slice(0, PROFILE_NAME_MAX)
  if (name && !list.some(x => x.id !== p.id && x.name.toLowerCase() === name.toLowerCase())) p.name = name
  p.bio = c.bio
  p.avatar = c.avatar
  SOCIALS.forEach(k => { p[k] = normUrl(c[k]) })
  p.updatedAt = now
}

/** Takes a profile out of the list (never the active one): the removed profile, or null */
export function removeProfile(state: ProfilesState, id: string): Profile | null {
  const p = state.list.find(x => x.id === id)
  if (!p || id === state.current) return null
  state.list = state.list.filter(x => x.id !== id)
  return p
}

/** What deleting a profile removes from this browser: its data (localStorage) and its save state (creatorOS_sync,
 *  `sync` is SYNC of src/stores/sync.ts, changed in place). Its images and voice notes (IndexedDB creatorOS_media), its online
 *  links and its files on Google Drive and in the backups are not touched */
export function forgetProfileLocal(id: string, sync: Record<string, unknown>): void {
  try { localStorage.removeItem(lsKey(id)) } catch { /* storage blocked */ }
  delete sync[id]
  try { localStorage.setItem(SYNC_KEY, JSON.stringify(sync)) } catch { /* storage full or blocked */ }
}
